/**
 * OwnDataBridgeStep - Transition screen between path selection and the connectors page.
 * Sets expectations for the 3-step own-data setup journey before dropping the user
 * into the product. Auto-continues after a short delay unless the user continues
 * immediately or pauses the timer (accessibility + enterprise control).
 */
import React from 'react';
import {
  Box, Typography, Stack, alpha, LinearProgress, Link, useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import LinkRounded from '@mui/icons-material/LinkRounded';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import { brandPalette } from '../../../theme/brandPalette';
import OnboardingLayout from '../components/OnboardingLayout';
import { config } from '../../../config';

const REDIRECT_SECONDS = 10;

interface OwnDataBridgeStepProps {
  onContinue: () => void;
  /** Return to sample vs own-data choice (try sample instead). */
  onTrySampleInstead?: () => void | Promise<void>;
  backNavLoading?: boolean;
  loading?: boolean;
}

const STEPS = [
  {
    Icon: LinkRounded,
    title: 'Add a connector',
    description: 'Connect Snowflake, BigQuery, Redshift, Postgres, or MySQL in under 2 minutes.',
  },
  {
    Icon: TableChartRounded,
    title: 'Select your tables',
    description: 'Pick the tables you want to analyse - we only read what you choose.',
  },
  {
    Icon: AccountTreeRounded,
    title: 'Build your data model',
    description: 'We map relationships across your tables so you can ask questions in plain English.',
  },
];

const DESKTOP_ICON_COLS = [1, 3, 5] as const;

function BridgeStepIcon({ Icon, index }: { Icon: React.ElementType; index: number }) {
  return (
    <Box sx={{ position: 'relative', width: 36, height: 36, flexShrink: 0 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          bgcolor: brandPalette.neutral[200],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon sx={{ fontSize: 18, color: brandPalette.neutral[600], display: 'block' }} />
      </Box>
      <Box
        component="span"
        sx={{
          position: 'absolute',
          top: -6,
          right: -6,
          width: 20,
          height: 20,
          borderRadius: '50%',
          bgcolor: brandPalette.primary[500],
          color: '#fff',
          fontSize: '0.6875rem',
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
          border: '2px solid',
          borderColor: 'background.paper',
          boxSizing: 'border-box',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {index + 1}
      </Box>
    </Box>
  );
}

export default function OwnDataBridgeStep({
  onContinue,
  onTrySampleInstead,
  backNavLoading = false,
  loading = false,
}: OwnDataBridgeStepProps) {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });
  const [secondsLeft, setSecondsLeft] = React.useState(REDIRECT_SECONDS);
  const [autoPaused, setAutoPaused] = React.useState(false);
  const triggeredRef = React.useRef(false);
  const onContinueRef = React.useRef(onContinue);
  onContinueRef.current = onContinue;

  const autoAdvance = !prefersReducedMotion && !autoPaused && !loading && !backNavLoading;

  const proceed = React.useCallback(() => {
    if (triggeredRef.current || loading || backNavLoading) return;
    triggeredRef.current = true;
    void onContinueRef.current();
  }, [loading, backNavLoading]);

  React.useEffect(() => {
    if (!autoAdvance) return;
    const id = window.setInterval(() => {
      setSecondsLeft((s: number) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [autoAdvance]);

  React.useEffect(() => {
    if (!autoAdvance || secondsLeft !== 0) return;
    proceed();
  }, [autoAdvance, secondsLeft, proceed]);

  const progressPct = prefersReducedMotion || autoPaused
    ? 0
    : ((REDIRECT_SECONDS - secondsLeft) / REDIRECT_SECONDS) * 100;

  return (
    <OnboardingLayout maxWidth={720}>
      {/* Header - tighter vertical rhythm */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 2.5 } }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: '12px',
          bgcolor: brandPalette.neutral[200],
          border: `1px solid ${brandPalette.neutral[300]}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mx: 'auto', mb: 1.5,
        }}>
          <CheckCircleOutlineRounded sx={{ color: brandPalette.primary[500], fontSize: 24 }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75, letterSpacing: -0.4, fontSize: { xs: '1.35rem', sm: '1.5rem' } }}>
          Let's connect your data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.45 }}>
          Three quick steps - then questions from your own data.
        </Typography>
      </Box>

      {/* 3-step journey: stacked on phones, horizontal row on sm+ */}
      <Box sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: brandPalette.neutral[300],
        bgcolor: 'background.paper',
        boxShadow: `0 1px 2px ${alpha(brandPalette.neutral[800], 0.04)}`,
        overflow: 'hidden',
        mb: { xs: 2, sm: 2.25 },
        p: { xs: 1.5, sm: 2 },
      }}>
        {/* Mobile: stacked rows */}
        <Stack direction="column" spacing={1.5} sx={{ display: { xs: 'flex', sm: 'none' } }}>
          {STEPS.map((step, idx) => (
            <Box
              key={idx}
              sx={{
                px: 1.5,
                py: 1.25,
                borderTop: idx > 0 ? `1px solid ${brandPalette.neutral[200]}` : 'none',
                pt: idx > 0 ? 2 : undefined,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <BridgeStepIcon Icon={step.Icon} index={idx} />
              <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', mb: 0.35, color: 'text.primary', lineHeight: 1.25 }}>
                  {step.title}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.45 }}>
                  {step.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>

        {/* Desktop: true grid - row 1 icons, row 2 titles, row 3 body (shared baselines) */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'grid' },
            gridTemplateColumns: 'minmax(0, 1fr) 28px minmax(0, 1fr) 28px minmax(0, 1fr)',
            gridTemplateRows: 'auto auto auto',
            columnGap: 0,
            rowGap: 1,
            alignItems: 'start',
            justifyItems: 'stretch',
            pt: 0.5,
          }}
        >
          {STEPS.map((step, idx) => (
            <Box
              key={`ic-${idx}`}
              sx={{
                gridColumn: DESKTOP_ICON_COLS[idx],
                gridRow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                pt: '6px',
              }}
            >
              <BridgeStepIcon Icon={step.Icon} index={idx} />
            </Box>
          ))}
          <Box
            sx={{
              gridColumn: 2,
              gridRow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'stretch',
              color: brandPalette.neutral[400],
              fontWeight: 700,
              fontSize: '0.9rem',
              userSelect: 'none',
            }}
            aria-hidden
          >
            →
          </Box>
          <Box
            sx={{
              gridColumn: 4,
              gridRow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'stretch',
              color: brandPalette.neutral[400],
              fontWeight: 700,
              fontSize: '0.9rem',
              userSelect: 'none',
            }}
            aria-hidden
          >
            →
          </Box>

          {STEPS.map((step, idx) => (
            <Box
              key={`ti-${idx}`}
              sx={{
                gridColumn: DESKTOP_ICON_COLS[idx],
                gridRow: 2,
                textAlign: 'center',
                px: 0.5,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'text.primary', lineHeight: 1.3, m: 0 }}>
                {step.title}
              </Typography>
            </Box>
          ))}

          {STEPS.map((step, idx) => (
            <Box
              key={`de-${idx}`}
              sx={{
                gridColumn: DESKTOP_ICON_COLS[idx],
                gridRow: 3,
                textAlign: 'center',
                px: 0.5,
              }}
            >
              <Typography
                color="text.secondary"
                sx={{
                  fontSize: '0.7rem',
                  lineHeight: 1.45,
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  m: 0,
                }}
              >
                {step.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Trust note */}
      <Typography variant="body2" sx={{
        textAlign: 'center', color: 'text.disabled',
        fontSize: '0.7rem', mb: 1.25, lineHeight: 1.4,
      }}>
        {config.brandName} reads your schema - your raw data never leaves your warehouse.
      </Typography>

      {onTrySampleInstead && (
        <Box sx={{ textAlign: 'center', mb: 1.5 }}>
          <Link
            component="button"
            type="button"
            disabled={backNavLoading}
            onClick={() => void onTrySampleInstead()}
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              cursor: backNavLoading ? 'default' : 'pointer',
              border: 'none',
              background: 'none',
              p: 0,
              fontFamily: 'inherit',
              textDecoration: 'underline',
              color: 'primary.main',
              '&:disabled': { color: 'text.disabled', textDecoration: 'none' },
            }}
          >
            Try a sample dataset instead →
          </Link>
        </Box>
      )}

      {/* Auto-redirect progress + CTA */}
      <Box sx={{ width: '100%' }}>
        {!prefersReducedMotion && !autoPaused && !loading && (
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={{
              height: 3,
              borderRadius: 1,
              mb: 1,
              bgcolor: alpha(brandPalette.primary[500], 0.12),
              '& .MuiLinearProgress-bar': {
                borderRadius: 1,
                bgcolor: brandPalette.primary[500],
              },
            }}
            aria-hidden
          />
        )}

        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{
            textAlign: 'center',
            minHeight: 32,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            mb: 1,
          }}
        >
          {loading ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
              Taking you to connectors…
            </Typography>
          ) : prefersReducedMotion ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.5 }}>
              When you’re ready, continue to add your first connector. Automatic redirect is off while reduced motion is enabled.
            </Typography>
          ) : autoPaused ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.5 }}>
              Automatic redirect is off. Continue when you’re ready using the button below.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.5 }}>
                Continuing to connectors in{' '}
                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {secondsLeft}
                </Box>
                {secondsLeft === 1 ? ' second' : ' seconds'}
                {' - '}or go now with the button below.
              </Typography>
              <Link
                component="button"
                type="button"
                onClick={() => setAutoPaused(true)}
                sx={{
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  p: 0,
                  fontFamily: 'inherit',
                  textDecoration: 'underline',
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                I need more time on this page
              </Link>
            </>
          )}
        </Box>

        <LoadingButton
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          loading={loading}
          onClick={proceed}
          endIcon={loading ? undefined : <ArrowForwardRounded />}
          sx={{
            py: 1.25, borderRadius: 2, fontWeight: 600,
            textTransform: 'none',
            bgcolor: brandPalette.primary[400],
            boxShadow: `0 1px 2px ${alpha(brandPalette.neutral[800], 0.08)}`,
            '&:hover': { bgcolor: brandPalette.primary[500] },
            mb: 0.5,
          }}
        >
          {loading ? 'Continuing…' : 'Continue to add connector'}
        </LoadingButton>
      </Box>
    </OnboardingLayout>
  );
}
